/**
 * @file Change log queries for entity audit history.
 *
 * Provides query options and hooks for fetching change history.
 */

import { useInfiniteQuery } from "@tanstack/react-query";
import { trpcClient } from "../trpc";

// ============================================================================
// Query Keys
// ============================================================================

export const changeLogKeys = {
  all: ["changeLog"] as const,
  entity: (organizationId: string, entityType: string, entityId: string) =>
    [...changeLogKeys.all, organizationId, entityType, entityId] as const,
  profile: (organizationId: string, profileId: string) =>
    [...changeLogKeys.all, organizationId, "profile", profileId] as const,
  organization: (organizationId: string) =>
    [...changeLogKeys.all, organizationId, "organization"] as const,
};

// Composite cursor type for pagination
export type ChangeLogCursor = { createdAt: string; id: string };

// ============================================================================
// Types
// ============================================================================

export interface ChangeLogEntry {
  id: string;
  entityType: string;
  entityId: string;
  operation: "create" | "update" | "delete";
  previousData: Record<string, unknown> | null;
  newData: Record<string, unknown> | null;
  changedFields: string[] | null;
  actorId: string | null;
  actorType: "user" | "system" | "api" | "migration";
  actor: { name: string; email: string } | null;
  reason: string | null;
  requestId: string | null;
  userAgent: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Infinite query hook for fetching entity change history.
 */
export function useChangeLogQuery(
  organizationId: string,
  entityType: string,
  entityId: string,
) {
  return useInfiniteQuery({
    queryKey: changeLogKeys.entity(organizationId, entityType, entityId),
    queryFn: async ({ pageParam }) => {
      return trpcClient.changeLog.getForEntity.query({
        organizationId,
        entityType,
        entityId,
        limit: 20,
        cursor: pageParam,
      });
    },
    initialPageParam: undefined as ChangeLogCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

/**
 * Infinite query hook for fetching profile change history.
 * Includes changes to profile and related entities (addresses, payments).
 */
/**
 * Infinite query hook for fetching organization change history.
 * Includes changes to organization and related entities (members, invitations, org memberships).
 */
export function useOrganizationChangeLogQuery(organizationId: string) {
  return useInfiniteQuery({
    queryKey: changeLogKeys.organization(organizationId),
    queryFn: async ({ pageParam }) => {
      return trpcClient.changeLog.getForOrganization.query({
        organizationId,
        limit: 20,
        cursor: pageParam,
      });
    },
    initialPageParam: undefined as ChangeLogCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}

/**
 * Infinite query hook for fetching profile change history.
 * Includes changes to profile and related entities (addresses, payments).
 */
export function useProfileChangeLogQuery(
  organizationId: string,
  profileId: string,
) {
  return useInfiniteQuery({
    queryKey: changeLogKeys.profile(organizationId, profileId),
    queryFn: async ({ pageParam }) => {
      return trpcClient.changeLog.getForProfile.query({
        organizationId,
        profileId,
        limit: 20,
        cursor: pageParam,
      });
    },
    initialPageParam: undefined as ChangeLogCursor | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });
}
